import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { memo, useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { Alert, AlertTitle, Box, Button, ButtonGroup, InputAdornment, Link, } from '@mui/material';
import { ColumnBox } from 'shared/components/containers';
import { issuesToMessages } from '../utils/issues-to-messages';
import { SupportChatMessageItem } from './message';
import { TextInputWrapper } from 'shared/components/fields/text';
import { LoadingButton } from '@mui/lab';
import { LiveHelp, Replay, StarRate, Telegram } from '@mui/icons-material';
import { ControlWithoutLabel } from 'shared/components/fields/control/without-label';
import { useAction, useAppSelector } from 'shared/hooks/redux/hooks';
import { getIssue, sendComment } from 'entities/support.chat/model/actions';
import { preventDefault } from 'console/shared/hooks/utils';
import { isSmallUI } from 'shared/app/lib/choose-ui-type';
import { useParams } from 'react-router-dom';
import { useBooleanState } from 'shared/hooks/state/boolean';
import { getRateTranslate, getStatusTranslate, getTokenFromUrl, } from 'features/support.chat/utils';
import { useMutationCallback } from 'shared/api/hooks';
import { useConfirmDialog } from 'shared/hooks/ui';
import { useRateDialog } from './rate-dialog';
import { useNavigateRoot } from 'shared/hooks/router';
const sx = {
    overflowX: 'hidden',
    overflowY: 'auto',
};
export const SupportChat = memo(function SupportChat() {
    const { issues, selected_issue_id, loading, error, login_link } = useAppSelector((state) => state.support_chat);
    const issue = useMemo(() => issues?.[selected_issue_id ?? -1], [issues, selected_issue_id]);
    const [loadingSend, noLoadingSend, setLoadingSend] = useBooleanState(false);
    const [message, setMessage] = useState('');
    const onChangeMessage = useCallback((event) => {
        setMessage(event);
        if (message == '') {
            // setFailDescription(true);
        }
    }, []);
    const { ticket_id } = useParams();
    const loadTicket = useAction(getIssue);
    useEffect(() => {
        if (!loading && issues == null && ticket_id != null) {
            loadTicket(parseInt(ticket_id, 10));
        }
    }, []);
    useEffect(() => {
        noLoadingSend();
    }, [issue]);
    const { msgs: messages } = useMemo(() => issue != null
        ? issuesToMessages(issue, login_link)
        : { msgs: [], attach_urls: {} }, [issue, login_link]);
    const _sendComment = useAction(sendComment);
    const onSentComment = useCallback(() => {
        if (message != '' && selected_issue_id != null) {
            setMessage('');
            _sendComment(selected_issue_id, message);
            setLoadingSend();
        }
    }, [selected_issue_id, message]);
    const onKeyDown = useCallback((ev) => {
        if (ev.ctrlKey && ev.key == 'Enter') {
            preventDefault(ev);
            onSentComment();
        }
    }, [onSentComment]);
    const messagesEndRef = useRef(null);
    const scrollToBottom = useCallback(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, []);
    useEffect(() => {
        setTimeout(scrollToBottom, 200);
    }, [messages]);
    const token = useMemo(() => {
        return getTokenFromUrl(login_link);
    }, [login_link]);
    const [RateDialog, open] = useRateDialog(selected_issue_id, token);
    const [onChangeIssueStatus, loadingChangeIssueStatus, , errorChangeIssueStatus] = useMutationCallback('support/set_issue_status');
    const navigateToRoot = useNavigateRoot();
    const _onReOpenIssue = useCallback(() => {
        if (ticket_id != null && token != null) {
            onChangeIssueStatus({
                status: 'opened',
                ticket_id: parseInt(ticket_id, 10),
                token,
            });
            navigateToRoot();
        }
    }, [ticket_id, token]);
    const _onCloseIssue = useCallback(() => {
        if (ticket_id != null && token != null) {
            onChangeIssueStatus({
                status: 'closed',
                ticket_id: parseInt(ticket_id, 10),
                token,
            });
        }
    }, [ticket_id, token]);
    const [ConfirmDialog, openConfirm] = useConfirmDialog();
    return (_jsxs(ColumnBox, { mx: 2, maxWidth: isSmallUI() ? '80dwmin' : '750px', mt: isSmallUI() ? 2 : undefined, children: [_jsxs(ColumnBox, { position: "relative", maxHeight: "55dvmax", sx: sx, width: "100%", height: "55vh", children: [messages.map((msg, index) => (_jsx(SupportChatMessageItem, { msg: msg }, `${msg.time}-${index}`))), _jsx("div", { ref: messagesEndRef })] }), (error != null || errorChangeIssueStatus != null) && (_jsxs(Alert, { severity: "error", children: [_jsx(AlertTitle, { children: "\u041D\u0435 \u0443\u0434\u0430\u043B\u043E\u0441\u044C \u0437\u0430\u043F\u0440\u043E\u0441\u0438\u0442\u044C \u0447\u0430\u0442" }), error ?? errorChangeIssueStatus] })), issue != null && issue.status == 'closed' && (_jsxs(Alert, { severity: "info", children: [_jsx(AlertTitle, { children: getStatusTranslate(issue.status) }), issue.rate != null && `C оценкой: ${getRateTranslate(issue.rate)}`] })), issue != null && issue.status != 'closed' && (_jsx(ControlWithoutLabel, { onChange: onChangeMessage, value: message, field: (inputs_props) => (_jsx(TextInputWrapper, { ...inputs_props, autoComplete: "off", multiline: true, endAdornment: _jsx(InputAdornment, { position: "end", children: _jsx(LoadingButton, { loading: loadingSend, color: "inherit", size: "small", onClick: onSentComment, children: _jsx(Telegram, {}) }) }), onKeyDown: onKeyDown })), validation_result: null, validation_warnings_result: null })), login_link != null && selected_issue_id != null && (_jsxs(Button, { color: "primary", variant: "outlined", startIcon: _jsx(LiveHelp, {}), fullWidth: true, component: Link, href: `${login_link}&redirect=/issues/${selected_issue_id}`, target: "_blank", rel: "noreferrer", underline: "always", children: ["\u041F\u0435\u0440\u0435\u0439\u0442\u0438 \u0432 Okdesk id:", selected_issue_id] })), issue != null && issue.status != 'closed' && (_jsx(Box, { pb: 2, children: _jsxs(ButtonGroup, { fullWidth: true, sx: { display: 'flex', gap: 8 }, children: [issue?.status == 'completed' && (_jsx(LoadingButton, { variant: "contained", loading: loadingChangeIssueStatus, startIcon: _jsx(Replay, {}), color: "warning", onClick: _onReOpenIssue, children: "\u0412\u043E\u0437\u043E\u0431\u043D\u043E\u0432\u0438\u0442\u044C" })), issue?.status == 'completed' && (_jsx(Button, { variant: "contained", startIcon: _jsx(StarRate, {}), color: "success", onClick: open, children: "\u0417\u0430\u043A\u0440\u044B\u0442\u044C \u0438 \u043E\u0446\u0435\u043D\u0438\u0442\u044C" }))] }) })), _jsxs(ConfirmDialog, { onConfirm: _onCloseIssue, children: ["\u0417\u0430\u043A\u0440\u044B\u0442\u044C \u0437\u0430\u044F\u0432\u043A\u0443\u00A0\u2116", ticket_id] }), _jsx(RateDialog, {})] }));
});
